import React, { useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import ContactModal from 'src/components/ContactModal';
import LineItemsCard from 'src/components/LineItemsCard';
import LoadingIndicator from 'src/components/LoadingIndicator';
import MapHeaderMatch from 'src/components/MapHeaderMatch';
import { ShipmentBanner } from 'src/components/ShipmentBanner';
import { OfferStatusBadge } from 'src/components/StatusBadge';
import heavyBoxRedImg from 'src/images/heavy-box-red.svg';
import { useAppState } from 'src/lib/hooks';
import { Badge, Input, Link } from 'straightline-ui';
import {
  AddressCardPreview,
  Button,
  Card,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  toast,
  useApp
} from 'straightline-ui';
import {
  formatMoney,
  toDollars,
  toPennies
} from 'straightline-utils/accounting';
import {
  ACCESSORIAL_CODES,
  OFFER_STATUS,
  OFFER_STATUSES,
  PROJECT_44
} from 'straightline-utils/constants';
import { differenceInSeconds, formatDate } from 'straightline-utils/time';

const OTHER_CODES = PROJECT_44.ACCESSORIAL_CODES.filter((code) => {
  const isPickup = ACCESSORIAL_CODES.PICKUP.find(
    (pickup) => pickup.value === code.value
  );
  const isDelivery = ACCESSORIAL_CODES.DELIVERY.find(
    (pickup) => pickup.value === code.value
  );
  return !isPickup && !isDelivery;
});

export default function ShipmentMatch({ match, location, history }) {
  const app = useApp();
  const { state: appState, setInState: setInAppState } = useAppState();
  const [isPostingTruck, setIsPostingTruck] = useState(false);

  const shipment_offer_id = location.query?.shipment_offer_id;
  const modal = location.query?.modal;
  const handleCloseModal = () => history.replace(location.pathname);

  const getCarrier = app.service('api/carrier').useFind(null, {
    keepPreviousData: true,
    refetchInterval: 5000,
    enabled: !appState.carrier.vetted
  });

  const carrier = appState.carrier.vetted
    ? appState.carrier
    : getCarrier.result;

  useEffect(() => {
    if (!appState.carrier.vetted && carrier?.vetted) {
      setInAppState('carrier', carrier);
    }
  }, [appState.carrier, carrier, setInAppState]);

  const getShipment = app.service('api/shipment-matches').useGet(
    match.params.id,
    {
      $include: [
        'pickup_address',
        'delivery_address',
        'shipment_offers',
        'shipment_requests'
      ]
    },
    {
      keepPreviousData: true,
      refetchInterval: (result) => {
        if (!result) {
          return false;
        }
        const created = differenceInSeconds(new Date(), result.created_at);
        if (created <= 15) {
          return 3000;
        }
        if (created <= 60) {
          return 8000;
        }
        if (created <= 60 * 5) {
          return 15000;
        }
        return 60000;
      }
    }
  );

  const { result: shipment } = getShipment;

  const handleMakeOffer = (data, formik) => {
    app
      .service('api/shipment-offers')
      .create({
        shipment_id: shipment.id,
        ...data
      })
      .then((offer) => {
        formik.setSubmitting(false);
        app.service('api/shipment-matches').invalidateGet(shipment.id);
        handleCloseModal();

        const color = offerStatusColors[offer.status].split('-')[1];
        toast(
          <div className="p-3">
            <p className={`lead font-weight-bold text-${color}`}>
              Offer {offerStatusLabels[offer.status]}
            </p>
            <div className="">{offerStatusTexts[offer.status]}</div>
          </div>,
          { hideProgressBar: true }
        );

        if (
          [OFFER_STATUS.ACCEPTED, OFFER_STATUS.COUNTERED].includes(offer.status)
        ) {
          history.replace(`${location.pathname}?shipment_offer_id=${offer.id}`);
        }
      })
      .catch((error) => {
        toast.errors(error);
        if (error.formik) {
          formik.setErrors(error.formik);
        }
        formik.setSubmitting(false);
      });
  };

  const handlePostTruck = () => {
    setIsPostingTruck(true);

    app
      .service('api/shipment-requests')
      .create({
        carrier_id: carrier.id,
        carrier_user_id: app.auth.user.id,
        truck_types: [shipment.truck_type],
        active: true,
        pickup_address: {
          ...shipment.pickup_address,
          radius: 150
        }
      })
      .then(() => {
        app.service('api/shipment-matches').invalidateGet(shipment.id);
        toast.success(
          `You have successfully posted a truck in ${shipment.pickup_address.city}, ${shipment.pickup_address.state}. You will receive notifications when new loads are available on this lane.`
        );
      })
      .catch((error) => {
        setIsPostingTruck(false);
        toast.errors(error);
      });
  };

  if (getShipment.isLoading || getCarrier.isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (getShipment.isError || getCarrier.isError) {
    return <Error error={getShipment.error || getCarrier.error} />;
  }

  if (shipment.carrier_id === carrier.id) {
    return <Redirect to={`/shipments/${getShipment.result.id}`} />;
  }

  const offerModal = shipment_offer_id
    ? shipment.shipment_offers?.find((offer) => offer.id === shipment_offer_id)
    : null;

  return (
    <div>
      {shipment.pickup_address.position &&
        shipment.delivery_address.position && (
          <MapHeaderMatch shipment={shipment} radius={50} />
        )}
      <Container fluid className="pb-3">
        <div className="header">
          <div className="header-body">
            <Row className="align-items-center justify-content-between">
              <Col lg="auto" className="mb-3 mb-lg-0">
                <h6 className="header-pretitle">Load Board / Book Load</h6>
                <Row className="align-items-center mb-n1">
                  <Col
                    xs="auto"
                    className="mb-2 pr-0 align-items-center d-flex"
                  >
                    <h1 className="mb-0 font-weight-bold d-inline-block mr-3">{`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}</h1>
                    <Icon
                      type="arrow-right"
                      style={{ fontSize: 24 }}
                      className="text-success lead d-inline-block font-weight-bold"
                    />
                  </Col>
                  <Col xs="auto" className="mb-2">
                    <h1 className="mb-0 font-weight-bold d-inline-block">{`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}</h1>
                  </Col>
                </Row>
              </Col>
              <Col lg="auto">
                <ContactModal
                  config={{
                    subject: `App Contact - Shipment #${shipment.id}`
                  }}
                >
                  {(toggle) => (
                    <Button color="light" className="mr-3" onClick={toggle}>
                      <Icon type="send" className="mr-2" />
                      Contact Straightline
                    </Button>
                  )}
                </ContactModal>
              </Col>
            </Row>
          </div>
        </div>
        <Card className="mx-n3 mb-n5 m-sm-0">
          <Row noGutters>
            <Col xl="6" className="">
              <div className="px-4 pt-4">
                <ShipmentBanner shipment={shipment} />
              </div>
              <div className="p-4">
                <ShipmentDetails shipment={shipment} />
                <LineItemsCard shipment={shipment} className="shadow-none" />
                <h5 className="text-uppercase text-muted font-weight-bold">
                  Notes
                </h5>
                <p className="pre-line mb-0">
                  {shipment.notes || <span className="text-muted">NA</span>}
                </p>
              </div>
            </Col>
            <Col xl="6" className="border-xl-left">
              {shipment.shipment_requests === 0 && (
                <div className="p-4 border-bottom">
                  <div>
                    Post a truck in {shipment.pickup_address.city},{' '}
                    {shipment.pickup_address.state} to receive notifications
                    when new loads are available on this lane. Posted trucks are
                    the best way to get matched with new loads and win more
                    business.
                  </div>
                  <Button
                    block
                    color="light"
                    type="button"
                    onClick={handlePostTruck}
                    disabled={isPostingTruck}
                    isLoading={isPostingTruck}
                    className={`mt-3 ${isPostingTruck ? 'is-loading' : ''}`}
                  >
                    Post truck in {shipment.pickup_address.city},{' '}
                    {shipment.pickup_address.state}
                  </Button>
                </div>
              )}
              {shipment.shipment_offers?.length > 0 ? (
                <ShipmentOffers carrier={carrier} shipment={shipment} />
              ) : (
                <ShipmentOffer carrier={carrier} shipment={shipment} />
              )}
            </Col>
          </Row>
        </Card>
        <BookLoadModal
          open={offerModal}
          offer={offerModal}
          shipment={shipment}
          toggle={handleCloseModal}
        />
        <CreateOfferModal
          open={modal === 'offer'}
          toggle={handleCloseModal}
          shipment={shipment}
          carrier={carrier}
          onSubmit={handleMakeOffer}
        />
      </Container>
    </div>
  );
}

const ShipmentDetails = ({ shipment }) => {
  const isToday = shipment.pickup_date === formatDate(new Date(), 'dateOnly');

  return (
    <ListGroup className="mx-n3 mx-sm-0 list-group list-group-flush list-group-activity mb-3">
      <ListGroupItem className="py-3">
        <Row>
          <Col xs="auto">
            <div className="avatar avatar-sm">
              <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                <Icon type="clipboard" />
              </div>
            </div>
          </Col>
          <Col className="ml-n2">
            <div className="font-weight-bold">
              {shipment.rate.service_level.name}
            </div>
            {shipment.rate.service_level.code === 'TEM' && (
              <div className="font-weight-bold text-primary">
                ** This shipment requires two drivers **
              </div>
            )}
            <div>
              <Icon type="clock" className="mr-3 text-secondary" />
              {shipment.rate.service_level.description}
            </div>
            <div>
              <Icon type="truck" className="mr-3 text-secondary" />
              {shipment.truck.label}
            </div>
            <AccessorialCodes shipment={shipment} codes={OTHER_CODES} />
            {shipment.accessorial_codes?.includes('HAZM') && (
              <div className="font-weight-bold text-warning mt-2 d-flex">
                <Icon type="alert-triangle" className="mr-2" />
                This load contains hazardous materials and requires Hazmat
                certification
              </div>
            )}
            {shipment.accessorial_codes?.includes('TANK') && (
              <div className="font-weight-bold text-warning mt-2 d-flex">
                <Icon type="alert-triangle" className="mr-2" />
                This load requires tanker endorsement certification
              </div>
            )}
          </Col>
        </Row>
      </ListGroupItem>
      <ListGroupItem className="py-3">
        <Row>
          <Col xs="auto">
            <div className="avatar avatar-sm">
              <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                <Icon type="truck" />
              </div>
            </div>
          </Col>
          <Col className="ml-n2">
            <div>
              <AddressCardPreview
                map={false}
                address={shipment.pickup_address}
                date={shipment.pickup_date}
                showCurrentTime={false}
                showStreet={false}
                showWeather
                showAppointment
                showDate
              />
              <div>
                <AccessorialCodes
                  shipment={shipment}
                  codes={ACCESSORIAL_CODES.PICKUP}
                />
              </div>
              {isToday && (
                <div className="text-primary font-weight-bold mt-2">
                  ** This load picks up today! **
                </div>
              )}
            </div>
          </Col>
        </Row>
      </ListGroupItem>
      <ListGroupItem className="py-3">
        <Row>
          <Col xs="auto">
            <div className="avatar avatar-sm">
              <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                <Icon type="map-pin" />
              </div>
            </div>
          </Col>
          <Col className="ml-n2">
            <div>
              <AddressCardPreview
                map={false}
                address={shipment.delivery_address}
                date={shipment.due_date}
                showCurrentTime={false}
                showStreet={false}
                showWeather
                showAppointment
                showDate
              />
              <div>
                <AccessorialCodes
                  shipment={shipment}
                  codes={ACCESSORIAL_CODES.DELIVERY}
                />
              </div>
            </div>
          </Col>
        </Row>
      </ListGroupItem>
    </ListGroup>
  );
};

const AccessorialCodes = ({ shipment, codes }) => {
  const items = codes.filter((code) => {
    return shipment.accessorial_codes?.includes(code.value);
  });

  if (!items.length) {
    return null;
  }

  return (
    <div className="mt-3">
      {items.map((item) => {
        return (
          <Badge
            pill
            key={item.value}
            color="soft-primary"
            className="mr-2 mb-1"
          >
            {item.label}
          </Badge>
        );
      })}
    </div>
  );
};

const BookLoadModal = ({ shipment, offer, open, toggle }) => {
  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      // onClosed={}
      contentClassName="h-100"
      modalClassName="modal-fullscreen-md"
      size="lg"
      scrollable
      centered
      fade={false}
    >
      {() => {
        return (
          <BookLoadModalContent
            shipment={shipment}
            offer={offer}
            toggle={toggle}
          />
        );
      }}
    </Modal>
  );
};

const OfferItem = ({ offer, children }) => {
  return (
    <div className="border rounded border-primary mb-4">
      <Row className="d-flex" noGutters>
        <Col
          md="4"
          className="p-0 border-md-right border-bottom border-md-bottom-0 border-primary"
        >
          <div
            className="position-relative w-100 h-100 rounded-left"
            style={{
              overflow: 'hidden',
              minHeight: 120
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                left: 0,
                bottom: '10%',
                overflow: 'hidden'
              }}
            >
              <div className="stripes" style={{ zIndex: 1 }}>
                <span className="animated slideInUp faster"></span>
                <span className="animated slideInDown faster"></span>
                <span className="animated slideInUp faster"></span>
                <span className="animated slideInDown faster"></span>
                <span className="animated slideInUp faster"></span>
              </div>
            </div>
          </div>
          <div
            style={{
              position: 'absolute',
              right: 0,
              left: 0,
              bottom: 0,
              overflow: 'hidden',
              zIndex: 1,
              padding: '0 15px'
            }}
          >
            <div className="card mb-0 px-3 py-4 shadow-sm rounded-bottom-0 animated fadeInUp fast">
              <div className="d-flex">
                <div>
                  <Icon
                    type="dollar-sign"
                    className="display-2"
                    style={{ lineHeight: 1 }}
                  />
                </div>
                <div className="ml-3 flex-grow-1">
                  <Row>
                    <Col xs="auto" md="12">
                      <div className="">
                        <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
                          Offer Amount
                        </h5>
                        <div className="lead" style={{ lineHeight: 1 }}>
                          {formatMoney(offer.offer_amount)}
                        </div>
                      </div>
                    </Col>
                    <Col xs="auto" md="12">
                      <div className="mt-0 mt-md-4">
                        <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
                          Offer Status
                        </h5>
                        <OfferStatusBadge status={offer.status} />
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col>
          {children}
          <div className="px-4 pb-4 pt-0">
            {offer.offer_notes && (
              <div className="mb-3">
                <h5 className="text-uppercase text-muted mb-1 font-weight-bold">
                  Offer Notes
                </h5>
                <div>{offer.offer_notes}</div>
              </div>
            )}
            {offer.counter_notes && (
              <div className="">
                <h5 className="text-uppercase text-muted mb-1 font-weight-bold">
                  Straightline Notes
                </h5>
                <div>{offer.counter_notes}</div>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

const BookLoadModalContent = ({ shipment, offer, toggle }) => {
  const app = useApp();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const { state: appState, setInState: setInAppState } = useAppState();

  const getCarrier = app.service('api/carrier').useFind(null, {
    keepPreviousData: true,
    refetchInterval: 5000,
    enabled: !appState.carrier.vetted
  });

  const carrier = appState.carrier.vetted
    ? appState.carrier
    : getCarrier.result;

  useEffect(() => {
    if (!appState.carrier.vetted && carrier?.vetted) {
      setInAppState('carrier', carrier);
    }
  }, [appState.carrier, carrier, setInAppState]);

  let offerAmount = null;
  if (offer) {
    offerAmount =
      offer.status === OFFER_STATUS.COUNTERED
        ? offer.counter_amount
        : offer.offer_amount;
  }

  const notValid =
    offer &&
    ![OFFER_STATUS.ACCEPTED, OFFER_STATUS.COUNTERED].includes(offer.status);

  const handleBookLoad = (e) => {
    setIsLoading(true);
    app
      .service('api/shipment-matches')
      .update(shipment.id, { shipment_offer_id: offer?.id })
      .then((shipment) => {
        history.push(`/shipments/${shipment.id}?modal=matched`);
        app.service('api/shipment-matches').invalidateFind();
        app.service('api/shipments').invalidateFind();
      })
      .catch((error) => {
        setIsLoading(false);
        toast.errors(error);
      });
  };

  return (
    <>
      <ModalBody>
        {shipment.pickup_address.position &&
          shipment.delivery_address.position && (
            <div className="mx-n4 mt-n4 mb-4">
              <MapHeaderMatch shipment={shipment} radius={50} />
            </div>
          )}
        {offer && (
          <div>
            {offer.status === OFFER_STATUS.ACCEPTED && (
              <OfferItem offer={offer}>
                <div className="p-4">
                  <div
                    className="h2 text-primary mb-3"
                    style={{ lineHeight: 1.5 }}
                  >
                    Straightline accepted your offer of{' '}
                    <strong>{formatMoney(offer.offer_amount)}</strong>
                  </div>
                  <div className="">
                    You can book this load at the offered rate. Act quickly to
                    lock in this price! Other carriers may still be making
                    offers and trying to win this load. Secure this load now
                    before someone else does.
                  </div>
                </div>
              </OfferItem>
            )}
            {offer.status === OFFER_STATUS.COUNTERED && (
              <OfferItem offer={offer}>
                <div className="p-4">
                  <div
                    className="h2 text-primary mb-3"
                    style={{ lineHeight: 1.5 }}
                  >
                    Straightline countered your offer with{' '}
                    <strong>{formatMoney(offer.counter_amount)}</strong>
                  </div>
                  <div className="">
                    You can book this load at the countered rate or make a new
                    offer. Review the notes below for more details.
                  </div>
                </div>
              </OfferItem>
            )}
            {offer.status === OFFER_STATUS.REJECTED && (
              <OfferItem offer={offer}>
                <div className="p-4">
                  <div
                    className="h2 text-danger mb-3"
                    style={{ lineHeight: 1.5 }}
                  >
                    Straightline rejected your offer of{' '}
                    <strong>{formatMoney(offer.offer_amount)}</strong>
                  </div>
                  <div className="mb-1">
                    Offers may be rejected for a variety of reasons. Other
                    carriers may have offered a better price, or the offer may
                    have been too high compared to market rates. Try making
                    another offer.
                  </div>
                </div>
              </OfferItem>
            )}
            {offer.status === OFFER_STATUS.PENDING && (
              <OfferItem offer={offer}>
                <div className="p-4">
                  <div
                    className="h2 text-warning mb-3"
                    style={{ lineHeight: 1.5 }}
                  >
                    Straightline is reviewing your offer of{' '}
                    <strong>{formatMoney(offer.offer_amount)}</strong>
                  </div>
                  <div className="mb-1">
                    Straightline is reviewing your offer. This process may take
                    some time. Offers are reviewed based on market rates, other
                    carrier offers, and shipment details. You will be notified
                    once a decision has been made.
                  </div>
                </div>
              </OfferItem>
            )}
          </div>
        )}
        <div className="mb-4">
          <ShipmentBanner shipment={shipment} amount={offerAmount} />
        </div>
        <div className="mb-4">
          <ShipmentDetails shipment={shipment} />
        </div>
        {!carrier.vetted && (
          <div className="p-4 border rounded border-danger mb-4">
            <div className="text-danger">
              Your business has not been vetted and you cannot book loads yet.
              Straightline is running a background check on your business. You
              will receive an email when this is complete.
            </div>
          </div>
        )}
        <div className="p-4 m-n4 bg-lighter">
          <div className="mb-4">
            Review all locations, appointments and accessorials before
            continuing. Booking confirms you as the carrier for this load and
            provides the Rate Confirmation, shipper contacts, and any available
            documents.
          </div>
          <div>
            By booking this load you accept all terms and conditions.
            Cancellations or no-shows may be subject to penalties. Visit the{' '}
            <Link to="/support">support page</Link> for more information.
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="justify-content-between">
        <Button color="white" onClick={toggle} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={!carrier.vetted || notValid || isLoading}
          loading={isLoading}
          onClick={handleBookLoad}
        >
          Book for{' '}
          {formatMoney(offerAmount || shipment.rate.price.carrier_price)}
        </Button>
      </ModalFooter>
    </>
  );
};

const CreateOfferModal = ({ shipment, carrier, open, toggle, onSubmit }) => {
  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      // onClosed={}
      // modalClassName="modal-fullscreen-sm"
      size="md"
      scrollable
      centered
      fade={false}
    >
      {() => {
        return (
          <ModalBody>
            <div className="mb-4">
              <ShipmentBanner shipment={shipment} />
            </div>
            <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
              Make Offer
            </h5>
            <p className="">
              Use your offers wisely. Straightline automatically accepts or
              counters most offers. You can make up to 3 offers per shipment.
              Provide your best offer for the highest chance of being accepted.
            </p>
            <OfferForm
              carrier={carrier}
              shipment={shipment}
              onSubmit={onSubmit}
            />
          </ModalBody>
        );
      }}
    </Modal>
  );
};

function getOfferSuggestion({ shipment_offers, rate }) {
  if (!Array.isArray(shipment_offers) || shipment_offers.length === 0) {
    return null;
  }

  const latestOffer = shipment_offers[shipment_offers.length - 1];
  const acceptedOffers = shipment_offers.filter(
    (offer) => offer.status === OFFER_STATUS.ACCEPTED
  );
  const counteredOffers = shipment_offers.filter(
    (offer) => offer.status === OFFER_STATUS.COUNTERED
  );

  const rounded = (amount) => {
    amount = toPennies(Math.round(toDollars(amount)));
    return Math.ceil(amount / 500) * 500;
  };

  if (latestOffer.status === OFFER_STATUS.REJECTED) {
    const text = `Your last offer was rejected. Offers are typically rejected when they are way too high compared to market rates or other carrier offers. Try a small increase to stay competitive.`;

    if (acceptedOffers.length > 0) {
      let amount = Math.max(
        ...acceptedOffers.map((offer) => offer.offer_amount)
      );

      amount = (amount + latestOffer.offer_amount) / 2;

      return {
        text,
        amount: rounded(amount)
      };
    }

    if (counteredOffers.length > 0) {
      let amount = Math.max(
        ...counteredOffers.map((offer) => offer.counter_amount)
      );

      amount = (amount + latestOffer.offer_amount) / 2;

      return {
        text,
        amount: rounded(amount)
      };
    }

    return {
      text: text,
      amount: rounded(rate.price.carrier_price * 1.05)
    };
  }

  if (latestOffer.status === OFFER_STATUS.COUNTERED) {
    const text = `Your last offer was countered so you are off to a good start! Make another offer somewhere between your last offer and the countered amount to see if you can get a few more dollars.`;

    if (acceptedOffers.length > 0) {
      let amount = Math.max(
        ...acceptedOffers.map((offer) => offer.offer_amount)
      );

      amount = (amount + latestOffer.offer_amount) / 2;

      return {
        text,
        amount: rounded(amount)
      };
    }

    if (counteredOffers.length > 0) {
      let amount = Math.max(
        ...counteredOffers.map((offer) => offer.counter_amount)
      );

      amount = (amount + latestOffer.offer_amount) / 2;

      return {
        text,
        amount: rounded(amount)
      };
    }

    return {
      text,
      amount: latestOffer.counter_amount * 1.05
    };
  }

  if (latestOffer.status === OFFER_STATUS.PENDING) {
    const text = `Your last offer is pending review. Offers typically only need review when they have some unusual details or are significantly higher than market rates. Consider making a slightly higher offer to improve your chances of being accepted.`;

    if (acceptedOffers.length > 0) {
      let amount = Math.max(
        ...acceptedOffers.map((offer) => offer.offer_amount)
      );

      amount = (amount + latestOffer.offer_amount) / 2;

      return {
        text,
        amount: rounded(amount)
      };
    }

    if (counteredOffers.length > 0) {
      let amount = Math.max(
        ...counteredOffers.map((offer) => offer.counter_amount)
      );

      amount = (amount + latestOffer.offer_amount) / 2;

      return {
        text,
        amount: rounded(amount)
      };
    }

    return {
      text: text,
      amount: rounded(rate.price.carrier_price * 1.05)
    };
  }

  if (latestOffer.status === OFFER_STATUS.ACCEPTED) {
    const text = `Your last offer was accepted. It's worth a shot to make a slightly higher offer to see if you can get a few more dollars. There's a chance Straightline may accept it!`;

    if (acceptedOffers.length > 0) {
      let amount = Math.max(
        ...acceptedOffers.map((offer) => offer.offer_amount)
      );

      amount = (amount + latestOffer.offer_amount) / 2;

      amount = amount === latestOffer.offer_amount ? amount * 1.01 : amount;

      return {
        text,
        amount: rounded(amount)
      };
    }

    if (counteredOffers.length > 0) {
      const counterAmount = Math.max(
        ...counteredOffers.map((offer) => offer.counter_amount)
      );

      const offerAmount = Math.max(
        ...counteredOffers.map((offer) => offer.offer_amount)
      );

      let amount = (counterAmount + offerAmount) / 2;

      if (amount === latestOffer.offer_amount) {
        amount = amount + 500;
      }

      return {
        text,
        amount: rounded(amount)
      };
    }

    return {
      text,
      amount: rounded(latestOffer.offer_amount * 1.05)
    };
  }

  return null;
}

const OfferForm = ({ carrier, shipment, onSubmit }) => {
  const app = useApp();
  const validationSchema = app.service('api/shipment-offers').schema;
  const offerSuggestion = getOfferSuggestion(shipment);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={{
        shipment_id: shipment.id,
        offer_amount: undefined,
        offer_notes: null
      }}
      validationSchema={validationSchema}
      render={({ isSubmitting, values }) => {
        const samePriceOffer = shipment.shipment_offers?.find(
          (offer) => Number(offer.offer_amount) === Number(values.offer_amount)
        );

        return (
          <Form>
            {shipment.shipment_offers?.length > 0 && (
              <div>
                <div className="mb-4">
                  <label>
                    You have {shipment.shipment_offers.length} other offer
                    {shipment.shipment_offers.length > 1 ? 's' : ''} on this
                    shipment
                  </label>
                  {shipment.shipment_offers.map((offer) => {
                    const status = OFFER_STATUSES.find(
                      (status) => status.value === offer.status
                    );
                    return (
                      <div key={offer.id} className="mb-2">
                        <div className="d-flex justify-content-between">
                          <div>
                            Offered {formatMoney(offer.offer_amount)}
                            {offer.counter_amount
                              ? ` (${formatMoney(offer.counter_amount)})`
                              : ''}{' '}
                            on {formatDate(offer.created_at, 'MMM d, yyyy')}
                          </div>
                          <div>
                            <Badge color={offerStatusColors[offer.status]}>
                              {status.label}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {offerSuggestion && (
                  <div className="mb-4">
                    <Label>Offer Suggestion</Label>
                    <div className="mb-4">{offerSuggestion.text}</div>
                    <Label>Suggested Amount</Label>
                    <h2 className="font-weight-bold text-primary mb-0">
                      {formatMoney(offerSuggestion.amount)}
                    </h2>
                  </div>
                )}
              </div>
            )}
            <FormGroup>
              <Label>Offer Notes</Label>
              <FormInput
                type="textarea"
                rows="4"
                name="offer_notes"
                disabled={isSubmitting}
              />
              <FormFeedback name="notes" />
            </FormGroup>
            {samePriceOffer && (
              <div className="mb-4 animated fadeIn fast text-danger">
                You have already made an offer of{' '}
                {formatMoney(samePriceOffer.offer_amount)} on this shipment.
                Consider making a different offer amount in order to get better
                rates.
              </div>
            )}
            <FormGroup className="mb-0">
              <Row className="">
                <Col className="pr-0">
                  <div className="d-flex justify-content-between">
                    <Label>Offer Amount</Label>
                  </div>
                  <InputGroup className="input-group-merge">
                    <FormInput
                      type="money"
                      name="offer_amount"
                      className="form-control-prepended form-control-appended"
                      disabled={isSubmitting}
                    />
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <Icon type="dollar-sign" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <InputGroupAddon addonType="append">
                      <InputGroupText>
                        {values.offer_amount && (
                          <>
                            {formatMoney(
                              Number(values.offer_amount) /
                                shipment.travel.miles
                            )}
                            /mile
                          </>
                        )}
                      </InputGroupText>
                    </InputGroupAddon>
                    <FormFeedback name="amount" />
                  </InputGroup>
                </Col>
                <Col xs="auto" className="text-right pt-4 mt-2">
                  <Button
                    block
                    color="primary"
                    disabled={isSubmitting}
                    className={`${isSubmitting ? 'is-loading' : ''}`}
                  >
                    <Icon type="dollar-sign" className="mr-2" />
                    Make Offer
                  </Button>
                </Col>
              </Row>
            </FormGroup>
            {!carrier.vetted && (
              <div className="text-danger mt-2">
                You may make an offer even though your business is not yet
                vetted. Once the vetting process is complete, your offer will be
                reviewed.
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

const offerStatusColors = {
  [OFFER_STATUS.PENDING]: 'soft-warning',
  [OFFER_STATUS.COUNTERED]: 'soft-info',
  [OFFER_STATUS.ACCEPTED]: 'soft-success',
  [OFFER_STATUS.REJECTED]: 'soft-danger'
};

const offerStatusTexts = {
  [OFFER_STATUS.PENDING]:
    'Straightline is reviewing your offer and will notify you soon. You can also make another offer.',
  [OFFER_STATUS.COUNTERED]:
    'Straightline has countered your offer. Click the button to review the details and book this load.',
  [OFFER_STATUS.ACCEPTED]:
    'Your offer has been accepted! Click the button to review the terms and book this load.',
  [OFFER_STATUS.REJECTED]:
    'This offer was rejected. Try making another offer with a lower price. Straightline will then review your new price and notify you.'
};

const offerStatusLabels = {
  [OFFER_STATUS.PENDING]: 'Pending',
  [OFFER_STATUS.COUNTERED]: 'Countered',
  [OFFER_STATUS.ACCEPTED]: 'Accepted',
  [OFFER_STATUS.REJECTED]: 'Rejected'
};

const OfferItems = ({ shipment, carrier }) => {
  const app = useApp();
  const [offer, setOffer] = useState(null);
  const [newOffer, setNewOffer] = useState(null);
  const offers = shipment.shipment_offers;

  const handleMakeOffer = (data, formik) => {
    app
      .service('api/shipment-offers')
      .create({
        shipment_id: shipment.id,
        ...data
      })
      .then((offer) => {
        formik.setSubmitting(false);
        app.service('api/shipment-matches').invalidateGet(shipment.id);
        setNewOffer(null);

        const color = offerStatusColors[offer.status].split('-')[1];
        toast(
          <div className="p-3">
            <p className={`lead font-weight-bold text-${color}`}>
              Offer {offerStatusLabels[offer.status]}
            </p>
            <div className="">{offerStatusTexts[offer.status]}</div>
          </div>,
          { hideProgressBar: true }
        );

        if (
          [OFFER_STATUS.ACCEPTED, OFFER_STATUS.COUNTERED].includes(offer.status)
        ) {
          setOffer(offer);
        }
      })
      .catch((error) => {
        toast.errors(error);
        if (error.formik) {
          formik.setErrors(error.formik);
        }
        formik.setSubmitting(false);
      });
  };

  return (
    <>
      <BookLoadModal
        open={!!offer}
        toggle={() => setOffer(null)}
        shipment={shipment}
        offer={offer}
      />
      <CreateOfferModal
        open={!!newOffer}
        toggle={() => setNewOffer(null)}
        shipment={shipment}
        carrier={carrier}
        onSubmit={handleMakeOffer}
      />
      <ListGroup>
        {offers.map((offer) => {
          const amount =
            offer.status === OFFER_STATUS.COUNTERED
              ? offer.counter_amount
              : offer.offer_amount;

          const status = OFFER_STATUSES.find(
            (status) => status.value === offer.status
          );

          return (
            <ListGroupItem className="px-4" key={offer.id}>
              <Row>
                <Col className="mb-3 mb-sm-0">
                  <div className="font-weight-bold">
                    Your offer of {formatMoney(offer.offer_amount)} is{' '}
                    <Badge color={offerStatusColors[offer.status]}>
                      {status.label}
                    </Badge>
                  </div>
                  <div className="mb-1">
                    {formatDate(offer.created_at, 'MMM d, yyyy h:mm a')}
                  </div>
                  <div className="text-secondary">
                    {offerStatusTexts[offer.status]}
                  </div>
                </Col>
                <Col sm="auto">
                  {[OFFER_STATUS.ACCEPTED, OFFER_STATUS.COUNTERED].includes(
                    offer.status
                  ) ? (
                    <Button
                      block
                      color="primary"
                      onClick={() => setOffer(offer)}
                    >
                      Book for {formatMoney(amount)}
                    </Button>
                  ) : (
                    <Button block color="white" onClick={() => setOffer(offer)}>
                      View Offer
                    </Button>
                  )}
                </Col>
              </Row>
            </ListGroupItem>
          );
        })}
        <ListGroupItem className="px-4">
          <Row>
            <Col className="mb-3 mb-sm-0">
              <div className="">You may make up to 3 offers per shipment.</div>
              <div className="text-secondary">
                Create a new offer if yours was rejected or you want to try
                another price. Better offers have a higher chance of being
                accepted.
              </div>
              {offers.length >= 3 && (
                <div className="text-danger mt-2">
                  You have reached the maximum number of offers for this load.
                </div>
              )}
            </Col>
            <Col sm="auto">
              <Button
                block
                color="outline-primary"
                disabled={offers.length >= 3}
                onClick={() => setNewOffer(true)}
              >
                Make Offer
              </Button>
            </Col>
          </Row>
        </ListGroupItem>
      </ListGroup>
    </>
  );
};

const ShipmentOffers = ({ carrier, shipment }) => {
  const hasAccepted = shipment.shipment_offers.find((offer) => {
    return [OFFER_STATUS.ACCEPTED, OFFER_STATUS.COUNTERED].includes(
      offer.status
    );
  });

  return (
    <div className="">
      <div className="p-4">
        <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
          Review Offers
        </h5>
        <div className="mb-2">
          Make up to 3 offers on this load. Straightline reviews offers and
          either accepts, rejects, or counters them. Book accepted offers
          instantly or review countered offers.
        </div>
        <div className="mb-4">
          Use your offers wisely! Straightline compares your offers to previous
          shipments, market conditions, other carriers' offers, and more.
        </div>
        <OfferItems shipment={shipment} carrier={carrier} />
      </div>
      <div className="p-4 border-top mt-auto">
        <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
          Book Load
        </h5>
        <BookNow
          price={shipment.rate.price.carrier_price}
          vetted={carrier.vetted}
          shipment={shipment}
          color={hasAccepted ? 'secondary' : 'primary'}
        />
      </div>
    </div>
  );
};

const ShipmentOffer = ({ carrier, shipment }) => {
  const app = useApp();
  const history = useHistory();
  const makeOffer = app.service('api/shipment-offers').useCreate();
  const [data, setData] = useState({
    cpm: 0,
    deadhead: 0
  });

  const miles = shipment.travel.miles + data.deadhead;
  const carrierCost = data.cpm * miles;

  const handleMakeOffer = (data, formik) => {
    makeOffer
      .create({
        shipment_id: shipment.id,
        ...data
      })
      .then((offer) => {
        formik.setSubmitting(false);
        app.service('api/shipment-matches').invalidateGet(shipment.id);

        const color = offerStatusColors[offer.status].split('-')[1];
        toast(
          <div className="p-3">
            <p className={`lead font-weight-bold text-${color}`}>
              Offer {offerStatusLabels[offer.status]}
            </p>
            <div className="">{offerStatusTexts[offer.status]}</div>
          </div>,
          { hideProgressBar: true }
        );

        if (
          [OFFER_STATUS.ACCEPTED, OFFER_STATUS.COUNTERED].includes(offer.status)
        ) {
          history.replace('?shipment_offer_id=' + offer.id);
        }
      })
      .catch((error) => {
        toast.errors(error);
        if (error.formik) {
          formik.setErrors(error.formik);
        }
        formik.setSubmitting(false);
      });
  };

  return (
    <div className="h-100">
      <div className="border-top border-xl-top-0">
        <div className="px-4 pt-4 pb-0">
          Enter your fixed cost per mile and deadhead to update the profit
          calculator below.
        </div>
        <Row noGutters>
          <Col xs="6" className="p-4">
            <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
              Fixed CPM
            </h5>
            <InputGroup className="input-group-merge">
              <Input
                type="money"
                className="form-control-prepended form-control-appended"
                placeholder="1.50"
                value={data.cpm || undefined}
                onChange={({ target: { value } }) => {
                  value = value || 0;
                  setData({
                    ...data,
                    cpm: Number(value)
                  });
                }}
              />
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <Icon type="dollar-sign" />
                </InputGroupText>
              </InputGroupAddon>
              <InputGroupAddon addonType="append">
                <InputGroupText>/mile</InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </Col>
          <Col xs="6" className="p-4">
            <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
              Deadhead
            </h5>
            <InputGroup className="input-group-merge">
              <Input
                type="number"
                className="form-control-appended"
                min="0"
                placeholder="25"
                value={data.deadhead || undefined}
                onChange={({ target: { value } }) => {
                  value = value || 0;
                  setData({
                    ...data,
                    deadhead: Number(value)
                  });
                }}
              />
              <InputGroupAddon addonType="append">
                <InputGroupText>miles</InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </Col>
        </Row>
        <Row noGutters className="border-top">
          <Col className="p-4 border-right">
            <h5 className="text-uppercase text-muted mb-1 font-weight-bold ">
              Payout
            </h5>
            <div className="lead text-primary">
              {formatMoney(shipment.rate.price.carrier_price)}
            </div>
            <div className="lead text-primary">
              {formatMoney(shipment.rate.price.carrier_price / miles)}
              <span className="text-secondary small">/mile</span>
            </div>
          </Col>
          <Col className="p-4 border-right">
            <h5 className="text-uppercase text-muted mb-1 font-weight-bold ">
              Cost
            </h5>
            <div
              className="lead text-danger animated fadeIn fast"
              key={`cost-${data.cpm}`}
            >
              {formatMoney(carrierCost)}
            </div>
            <div
              className="lead text-danger animated fadeIn fast"
              key={`cost-cpm-${data.cpm}`}
            >
              {formatMoney(carrierCost / miles)}
              <span className="text-secondary small">/mile</span>
            </div>
          </Col>
          <Col className="p-4">
            <h5 className="text-uppercase text-muted mb-1 font-weight-bold ">
              Profit
            </h5>
            <div
              className="lead text-success animated fadeIn fast"
              key={`profit-${data.cpm}`}
            >
              {formatMoney(shipment.rate.price.carrier_price - carrierCost)}
            </div>
            <div
              className="lead text-success animated fadeIn fast"
              key={`profit-cpm-${data.cpm}`}
            >
              {formatMoney(
                (shipment.rate.price.carrier_price - carrierCost) / miles
              )}
              <span className="text-secondary small">/mile</span>
            </div>
          </Col>
        </Row>
      </div>
      <div className="p-4 border-top">
        <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
          Make Offer
        </h5>
        <p className="">
          You may make counteroffers to this price. Straightline will review
          your offer and get back to you ASAP! If your offer is accepted, you
          will be able to book this load immediately at that price.
        </p>
        <OfferForm
          shipment={shipment}
          carrier={carrier}
          onSubmit={handleMakeOffer}
        />
      </div>
      <div className="p-4 border-top mt-auto">
        <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
          Book Load
        </h5>
        <BookNow
          price={shipment.rate.price.carrier_price}
          vetted={carrier.vetted}
          shipment={shipment}
          color="primary"
        />
      </div>
    </div>
  );
};

const BookNow = ({ vetted, price, shipment, color }) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const bookNowRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        threshold: 0.1 // Trigger when 10% of the element is visible
      }
    );

    if (bookNowRef.current) {
      observer.observe(bookNowRef.current);
    }

    return () => {
      if (bookNowRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(bookNowRef.current);
      }
    };
  }, [bookNowRef]);

  return (
    <div ref={bookNowRef}>
      <BookLoadModal
        open={modalOpen}
        shipment={shipment}
        toggle={() => setModalOpen(null)}
      />
      {vetted ? (
        <div>
          <p>
            By booking this load you accept all terms and conditions. After
            booking, you will have access to your Rate Confirmation, shipment
            contacts, documents and other tools.
          </p>
          {shipment.rush && (
            <div
              style={{ lineHeight: 1.3 }}
              className={`text-danger h2 mb-4 d-flex animated ${
                isIntersecting ? 'flash slow' : ''
              }`}
            >
              <Icon type="clock" className="mr-2" />
              This load is in high demand and has offers from other carriers.
              Act fast to book this load before someone else does!
            </div>
          )}
          <Button
            onClick={() => setModalOpen(true)}
            color={color}
            block
            style={{ fontSize: '1.25rem' }}
          >
            Book for <strong>{formatMoney(price)}</strong>
          </Button>
        </div>
      ) : (
        <div>
          <p className="text-danger">
            Your business has not been vetted and you cannot book loads yet.
            Straightline is running a background check on your business. You
            will receive an email when this is complete.
          </p>
          <Button color={color} block disabled style={{ fontSize: '1.25rem' }}>
            Book for <strong>{formatMoney(price)}</strong>
          </Button>
        </div>
      )}
    </div>
  );
};

const Error = ({ error }) => {
  return (
    <Container>
      <Row className="align-items-center justify-content-center vh-100">
        <Col lg="6" className="text-center">
          <img
            src={heavyBoxRedImg}
            alt="Not Found"
            className="img-fluid animated fadeIn faster mb-5"
          />
          {error.code === 404 ? (
            <div>
              <h1>This load is no longer available</h1>
              <p className="lead">
                Most likely another carrier accepted the load before you. It's
                also possible that the load was cancelled by the shipper.
              </p>
            </div>
          ) : (
            <div>
              <h1>Error</h1>
              <p className="lead">
                {error.message || 'There was an unexpected error'}
              </p>
            </div>
          )}
          <Button
            to="/shipment-matches"
            color="primary"
            size="lg"
            className="mr-3"
          >
            <Icon type="clipboard" className="mr-2" />
            Load Board
          </Button>
          <Button to="/support" color="secondary" size="lg">
            <Icon type="send" className="mr-2" />
            Contact Straightline
          </Button>
        </Col>
      </Row>
    </Container>
  );
};
